import type { Map as MapboxMap } from 'mapbox-gl';
import type { RefObject } from 'react';
import type { ILocation } from '@/types';
import { LayerId, MarkKind, SourceId } from '../constants';
import { MapLayersService } from '../utils/MapLayersService';

/**
 * The workspace's location pins. `MapLayersService` owns the clustered source they feed, shared
 * with the mirrored deals; this hook owns the unclustered pin and the selected one.
 */
export function useClusters(map: RefObject<MapboxMap | null>, locations: ILocation[]) {
  function setupLocationsClusters() {
    const currentMap = map.current;
    if (!currentMap) return;

    const service = new MapLayersService(currentMap);
    service.addClusteredMarksLayer();

    if (!currentMap.getSource(SourceId.SELECTED_LOCATION)) {
      currentMap.addSource(SourceId.SELECTED_LOCATION, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!currentMap.getLayer(LayerId.LOCATION_UNCLUSTERED)) {
      currentMap.addLayer({
        id: LayerId.LOCATION_UNCLUSTERED,
        type: 'symbol',
        source: SourceId.CLUSTERED_MARKS,
        // The kind keeps a deal from drawing a location pin off the same source.
        filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'kind'], MarkKind.LOCATION]],
        layout: {
          'icon-image': 'marker-location',
          'icon-size': 1.1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
          'symbol-sort-key': ['get', 'sortKey'],
        },
      });
    }

    // No filter and no cluster: this source only ever holds the selected location.
    if (!currentMap.getLayer(LayerId.SELECTED_LOCATION_MARK)) {
      currentMap.addLayer({
        id: LayerId.SELECTED_LOCATION_MARK,
        type: 'symbol',
        source: SourceId.SELECTED_LOCATION,
        layout: {
          'icon-image': 'marker-location-selected',
          'icon-size': 1.1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
        },
      });
    }

    service.addLocationStationModels();
  }

  /** The set alone, so a change never costs a teardown of the source the deals share. */
  function applyLocations() {
    const currentMap = map.current;
    if (!currentMap) return;
    new MapLayersService(currentMap).setLocationsData(locations);
  }

  function cleanupLocationsClusters() {
    const currentMap = map.current;
    if (!currentMap) return;

    if (currentMap.getLayer(LayerId.LOCATION_UNCLUSTERED))
      currentMap.removeLayer(LayerId.LOCATION_UNCLUSTERED);
    if (currentMap.getLayer(LayerId.SELECTED_LOCATION_MARK))
      currentMap.removeLayer(LayerId.SELECTED_LOCATION_MARK);
    new MapLayersService(currentMap).removeLocationStationModels();
    if (currentMap.getSource(SourceId.SELECTED_LOCATION))
      currentMap.removeSource(SourceId.SELECTED_LOCATION);
    // Emptying, not removing: the shared source is the service's, and the deals still need it.
    new MapLayersService(currentMap).setLocationsData([]);
  }

  function applySelection(selectedLocationId: string | null) {
    const currentMap = map.current;
    if (!currentMap) return;
    new MapLayersService(currentMap).setSelectedLocation(selectedLocationId);
  }

  return { setupLocationsClusters, cleanupLocationsClusters, applyLocations, applySelection };
}
